import { DateTime } from "luxon";

import { formatFixed, formatPercent } from "../../../utilities/format";
import i18n from "../../../i18n";
import { PlantStatusMachineDTO } from "../../../DTOs/Dashboards/PlantStatusCardDataDTO";
import { ShiftLetter, TripleShift } from "../../../types/Shift";
import { shiftIndex } from "../../../utilities/helpers/shiftLockStateHelpers";

const UP = "UP";

const isUp = (machine: PlantStatusMachineDTO) => (machine.status || UP) === UP;

/** Down machines first, then the lowest availability, so the reason a tile is red reads top down. */
export const sortPlantStatusMachines = (machines: PlantStatusMachineDTO[]): PlantStatusMachineDTO[] =>
  [...machines].sort((a, b) => {
    if (isUp(a) !== isUp(b)) return isUp(a) ? 1 : -1;

    const availability = (machine: PlantStatusMachineDTO) => machine.availabilityPercentage ?? -1;
    if (availability(a) !== availability(b)) return availability(a) - availability(b);

    return a.plantRef.localeCompare(b.plantRef);
  });

export const machineStatusColour = (machine: PlantStatusMachineDTO): string =>
  isUp(machine) ? "var(--theme-success)" : machine.statusColour || "var(--theme-warning)";

export type ShiftWindow = { start: DateTime; end: DateTime };

/**
 * When a stoppage started in the shift on screen its start time is the useful reading; one that
 * carried over from an earlier shift needs its length instead, because a bare time would not say
 * which shift it belongs to.
 */
export const machineStatusTiming = (
  machine: PlantStatusMachineDTO,
  shift: ShiftWindow,
  now: DateTime
): string | null => {
  const since = machine.statusSince;
  if (isUp(machine) || !since) return null;

  const startedInShift = shift.start.isValid && shift.end.isValid && since >= shift.start && since < shift.end;
  if (startedInShift) return i18n.t("plant.since", { ns: "dashboard", time: since.toFormat("HH:mm") });

  const hours = Math.max(0, now.diff(since, "hours").hours);
  return `${formatFixed(hours, 1)}h`;
};

export const formatMachinePercent = (value?: number | null): string =>
  value == null ? "-" : formatPercent(Math.round(value));

export type AvailabilityBand = "success" | "warning" | "danger";

const FALLBACK_SUCCESS = 0.7;
const FALLBACK_WARNING = 0.65;
const WARNING_MARGIN_POINTS = 5;

export const availabilityBand = (availability: number, targetPercentage: number): AvailabilityBand => {
  if (targetPercentage <= 0)
    return availability >= FALLBACK_SUCCESS
      ? "success"
      : availability >= FALLBACK_WARNING
        ? "warning"
        : "danger";

  const percentage = availability * 100;

  if (percentage >= targetPercentage) return "success";

  return percentage >= targetPercentage - WARNING_MARGIN_POINTS ? "warning" : "danger";
};

// Note: widening only has something to add once another shift exists on that date. An afternoon or night
// shift always follows the day shift; for a day shift the next one may not have happened yet, which is
// the one case that locks the choice off. A future date holds no shifts at all. Until the latest
// shift is known it stays unlocked, because widening a date that holds one shift returns that shift's
// own figures rather than anything wrong.
export const resolveShiftScope = (
  selectedDate: DateTime,
  selectedShift: TripleShift,
  wantsFullDay: boolean,
  latestShift: { date: DateTime; dayOrNight: ShiftLetter } | null
) => {
  const dayShift = shiftIndex(selectedDate, "D");
  const latest = latestShift === null ? null : shiftIndex(latestShift.date, latestShift.dayOrNight);
  const locked = latest !== null && (selectedShift === "DAY" ? latest <= dayShift : latest < dayShift);

  return { fullDay: !locked && wantsFullDay, locked };
};
